import { Box, Button, createTheme, Dialog, Stack } from "@mui/material";
import React, { type FC, useEffect } from "react";
import * as yup from "yup";

import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { heIL } from "@mui/x-date-pickers/locales";
import { missionRepo } from "@repositories";
import {
  type Mission,
  type TaskPriority,
  type TaskStatus,
  type TaskTypes,
} from "@shared/entities/mission/entity";
import dayjs from "dayjs";
import { FormSelect } from "../../../components/form-components/form-select-input/FormSelect";
import { FormTextField } from "../../../components/form-components/form-text-field/FormTextField";
import { useAlert } from "../../../contexts/AlertProvider";
import { useOrganization } from "../../../contexts/OrganizationProvider";
import { useOrganizationManagerOptions } from "../../../queries/users.query";
import FormProvider, { useForm } from "../../../helpers/forms/FormProvider";

import { useUser } from "../../../contexts/UserProvider";
import { provide } from "../../../helpers/wrap-and-provide";

import MissionConnectionField from "./MissionConnectionField";
import "./MissionDialog.scss"; // Reuse styles if applicable

export const taskPriorityOptions = ["רגילה", "גבוהה", "דחופה", ""] as const;
export const taskStatusOptions = ["לא בוצעה", "בתהליך", "בוצעה", ""] as const;

interface MissionFormProps {
  mission?: Partial<Mission>;
  onSuccess: () => void;
  open: boolean;
  onClose: () => void;
  missionType?: TaskTypes;
}

const validationSchema = yup.object().shape({
  description: yup
    .string()
    .min(5, "תיאור המטלה חייב להכיל לפחות 5 תווים")
    .required("שדה חובה"),
  date: yup.date().required("שדה חובה"),
  priority: yup
    .string()
    .oneOf(taskPriorityOptions, "ערך לא חוקי")
    .required("שדה חובה"),
  status: yup.string().oneOf(taskStatusOptions, "ערך לא חוקי"),
});

const DEFAULT_VALUES: Partial<Mission> = {
  description: "",
  date: new Date(),
  priority: "רגילה" as TaskPriority, // Allow empty value
  status: "לא בוצעה" as TaskStatus, // Allow empty value
  associationId: "",
  groupId: "",
  opportunityId: "",
  teamId: "",
  assignedManagerId: "",
  comments: "",
  types: [] as TaskTypes[],
};

const theme = createTheme({
  palette: {
    primary: {
      main: "#05204CF2",
    },
  },
});

export const MissionForm: FC<MissionFormProps> = ({
  mission,
  onSuccess,
  onClose,
  open,
  missionType,
}) => {
  const { setAlertText, setSeverity } = useAlert();
  const { resetForm, values, isValid, setValues } = useForm<
    Partial<Mission> & { isAidRelated?: boolean; }
  >();
  const { currOrganization } = useOrganization();
  const { data: organizationManagers = [] } = useOrganizationManagerOptions();
  const [isLoading, setIsLoading] = React.useState(false);
  const isEditMode = Boolean(mission?.id);
  const { currentUser } = useUser();

  useEffect(() => {
    if (mission?.id) {
      const initialValues = {
        description: mission.description || "",
        date: mission.date || new Date(),
        priority: mission.priority || "", // Default to empty string if undefined
        status: mission.status || "", // Default to empty string if undefined
        associationId: mission.associationId || "",
        groupId: mission.groupId || "",
        opportunityId: mission.opportunityId || "",
        teamId: mission.teamId || "",
        assignedManagerId: mission.assignedManagerId || "",
        comments: mission.comments || "",
      };

      // Only set values if they've actually changed
      if (JSON.stringify(values) !== JSON.stringify(initialValues)) {
        setValues(initialValues as any);
      }
    }

    if (mission) {
      setValues({
        ...DEFAULT_VALUES,
        ...mission,
        types: isEditMode
          ? mission.types?.length
            ? Array.from(
              new Set([...mission.types, missionType || ("" as const)]),
            )
            : [missionType || ("" as const)]
          : [missionType || ("" as const)],
      });
    }
  }, [mission]); // Remove setValues from dependencies

  const saveMission = async () => {
    if (!isValid || isLoading) return;
    const {
      associationId,
      teamId,
      groupId,
      opportunityId,
      isAidRelated = false,
      ...restValues
    } = values;
    try {
      setIsLoading(true);
      const types: TaskTypes[] = [];
      if (teamId) types.push("TEAM");
      if (groupId) types.push("GROUP");
      if (associationId && isAidRelated) types.push("AID_RELATED");
      if (associationId && !isAidRelated) types.push("USER");
      if (opportunityId) types.push("OPPORTUNITY");

      await missionRepo.save({
        ...(mission || {}),
        ...restValues,
        creatorManagerId: isEditMode
          ? mission?.creatorManagerId
          : currentUser?.id,
        types,
        organization: currOrganization,
      });
      setAlertText(isEditMode ? "המטלה עודכנה בהצלחה" : "המטלה נוצרה בהצלחה");
      setSeverity("success");
      resetForm();
      onSuccess();
    } catch (error) {
      setAlertText(
        isEditMode ? "אירעה שגיאה בעדכון המטלה" : "אירעה שגיאה ביצירת המטלה",
      );
      setSeverity("error");
      console.error("Error saving mission:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog
      className="mission-form"
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <Box className="mission-form__header" sx={{ padding: "16px" }}>
        <h2>{isEditMode ? "עריכת מטלה" : "יצירת מטלה חדשה"}</h2>
      </Box>

      <Stack
        spacing={2}
        className="mission-form__content"
        sx={{ padding: "16px" }}
      >
        <FormTextField
          name="description"
          label="תיאור"
          multiline
          rows={3}
          fullWidth
        />

        <LocalizationProvider
          dateAdapter={AdapterDayjs}
          localeText={
            heIL.components.MuiLocalizationProvider.defaultProps.localeText
          }
          adapterLocale="he"
        >
          <DateTimePicker
            label="תאריך ושעה"
            format="DD/MM/YYYY HH:mm"
            value={dayjs(values?.date)}
            onChange={(newValue) => {
              if (newValue) {
                setValues((prevValues) => ({
                  ...prevValues,
                  date: newValue.toDate(),
                }));
              }
            }}
            slotProps={{
              textField: {
                fullWidth: true,
                className: "field",
              },
            }}
            className="date-input"
          />
        </LocalizationProvider>

        <FormSelect
          name="priority"
          label="דחיפות"
          options={["", ...taskPriorityOptions.filter(Boolean)]} // Allow empty value
          value={values.priority || ""} // Ensure controlled behavior
        />

        <FormSelect
          name="status"
          label="סטטוס"
          options={["", ...taskStatusOptions.filter(Boolean)]} // Allow empty value
          value={values.status || ""} // Ensure controlled behavior
        />

        <FormSelect
          name="assignedManagerId"
          label="מנהל אחראי"
          options={
            organizationManagers.filter(Boolean).map((manager) => ({
              content: manager.name,
              value: manager.id,
            }))
          } // Allow empty value
          value={values.assignedManagerId || ""} // Ensure controlled behavior
        />

        <MissionConnectionField
          mission={mission}
          onChange={(connections) => {
            setValues((prev) => ({
              ...prev,
              teamId: connections.teamId || "",
              groupId: connections.groupId || "",
              associationId: connections.associationId || "",
              opportunityId: connections.opportunityId || "",
              isAidRelated: !!connections.isAidRelated,
            }));
          }}
        />

        <FormTextField
          name="comments"
          label="הערות"
          multiline
          rows={2}
          fullWidth
        />
      </Stack>

      <Box className="mission-form__footer" sx={{ padding: "16px" }}>
        <Button onClick={() => resetForm()} variant="outlined">
          ביטול
        </Button>
        <Button
          variant="contained"
          onClick={saveMission}
          disabled={!isValid || isLoading}
        >
          {isEditMode ? "שמירה" : "יצירה"}
        </Button>
      </Box>
    </Dialog>
  );
};

export default provide([
  FormProvider<Partial<Mission>>,
  {
    initialValues: DEFAULT_VALUES,
    validationSchema,
    onSubmit: () => { },
  },
])(MissionForm);
